// Products.js

import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import Dropdown2 from "./Dropdown2";
import "../style/Products.css";
import { useTranslation } from "react-i18next";
import { db } from "../firebase-config";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import {
  CurrencyContext,
  pricePlusSurcharge,
} from "../context/CurrencyContext";

const Products = () => {
  const { t, i18n } = useTranslation();
  const { currency, exchangeRates } = useContext(CurrencyContext);

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [productsToShowCount, setProductsToShowCount] = useState(48);
  const [categories, setCategories] = useState([]);

  // Brand states
  const [brands, setBrands] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const q = query(
          collection(db, "categories"),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const fetchedCategories = querySnapshot.docs.map((doc) => doc.id);
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Fetch products and collect brands
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);

        const fetchedProducts = new Map();
        const brandMap = new Map();

        // Iterate over categories fetched from the 'categories' collection
        for (const category of categories) {
          const itemsCollection = collection(db, "products", category, "items");
          const q = query(itemsCollection, orderBy("createdAt", "desc"));
          const productsSnapshot = await getDocs(q);

          productsSnapshot.forEach((doc) => {
            const productData = doc.data();
            const createdAt = productData.createdAt
              ? productData.createdAt.toDate()
              : new Date(0);

            // Skip products with visibility "No"
            if (productData.categories[0].visibility === "No") {
              return;
            }

            // Prepare a key to uniquely identify the product
            const productKey =
              productData.uuid || productData.productId || doc.id;

            // If a product with the same key already exists, merge categories
            if (fetchedProducts.has(productKey)) {
              const existingProduct = fetchedProducts.get(productKey);
              existingProduct.categories.push({
                category: category,
                visibility: productData.categories[0].visibility,
              });
            } else {
              fetchedProducts.set(productKey, {
                id: doc.id,
                uuid: productData.uuid || null,
                name: productData.name || "Unknown Product",
                price: `£${productData.price || "0"}`,
                displayImages: productData.displayImages || [],
                imageUrl: productData.displayImages?.[0] || "",
                colors: productData.colors || [],
                brandName: productData.brandName || "",
                categories: [
                  {
                    category: category,
                    visibility: productData.categories[0].visibility || "All",
                  },
                ],
                selectedProductId: productData.productId || "",
                createdAt,
                newIn: productData.newIn || "",
              });
            }

            // Collect brand names
            if (productData.brandName && productData.brandName.trim() !== "") {
              if (!brandMap.has(productData.brandName)) {
                brandMap.set(productData.brandName, {
                  name: productData.brandName,
                });
              }
            }
          });
        }

        // Convert Map to array and sort by createdAt descending
        const productsArray = Array.from(fetchedProducts.values()).sort(
          (a, b) => b.createdAt - a.createdAt
        );

        setProducts(productsArray);
        setFilteredProducts(productsArray);
        setDisplayedProducts(productsArray.slice(0, productsToShowCount));

        // Set brands
        setBrands(Array.from(brandMap.values()));
        setAvailableBrands(["Select All", ...Array.from(brandMap.keys())]);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError("Failed to load products. Please try again later.");
        setIsLoading(false);
      }
    };

    if (categories.length > 0) {
      fetchProducts();
    }
  }, [categories]);

  // Apply filters
  useEffect(() => {
    const applyFilters = () => {
      let filtered = products;

      // Apply visibility logic
      filtered = filtered.filter((product) => {
        const productVisibilities = product.categories.map(
          (cat) => cat.visibility
        );

        // If the product has visibility "No" in all categories, exclude it
        if (productVisibilities.every((vis) => vis === "No")) {
          return false;
        }

        // Handle visibility "All"
        if (productVisibilities.includes("All")) {
          return true;
        }

        // Handle visibility "Limited"
        if (productVisibilities.includes("Limited")) {
          if (
            selectedCategories.length === 0 ||
            selectedCategories.includes("Select All")
          ) {
            return false;
          }

          const productCategories = product.categories
            .filter((cat) => cat.visibility === "Limited")
            .map((cat) => cat.category);

          const isInSelectedCategory = selectedCategories.some((selectedCat) =>
            productCategories.includes(selectedCat)
          );

          return isInSelectedCategory;
        }

        return false;
      });

      // Apply category filter
      if (
        selectedCategories.length > 0 &&
        !selectedCategories.includes("Select All")
      ) {
        filtered = filtered.filter((product) => {
          const productCategories = product.categories.map(
            (cat) => cat.category
          );
          return selectedCategories.some((selectedCat) =>
            productCategories.includes(selectedCat)
          );
        });
      }

      // Apply brand filter
      if (selectedBrands.length > 0 && !selectedBrands.includes("select_all")) {
        filtered = filtered.filter((product) => {
          return selectedBrands.includes(product.brandName);
        });
      }

      setFilteredProducts(filtered);
      setDisplayedProducts(filtered.slice(0, productsToShowCount));
    };

    applyFilters();
  }, [selectedCategories, selectedBrands, products, productsToShowCount]);

  // Update displayed products when productsToShowCount changes
  useEffect(() => {
    setDisplayedProducts(filteredProducts.slice(0, productsToShowCount));
  }, [filteredProducts, productsToShowCount]);

  // Reset selectedBrands if they are no longer available
  useEffect(() => {
    if (selectedBrands.length > 0 && !selectedBrands.includes("select_all")) {
      const validSelectedBrands = selectedBrands.filter((brand) =>
        availableBrands.includes(brand)
      );
      if (validSelectedBrands.length !== selectedBrands.length) {
        setSelectedBrands(validSelectedBrands);
      }
    }
  }, [availableBrands, selectedBrands]);

  const handleProductClick = (product) => {
    window.scrollTo(0, 0);
    const categoryName = product.categories[0].category;

    const url = `/full-product?category=${encodeURIComponent(
      categoryName
    )}&productId=${encodeURIComponent(product.id)}`;

    window.location.href = url;
  };

  // Prepare category options with "Select All" as the first item
  const categoryOptions = ["Select All", ...categories].map((cat) => t(cat));

  // Prepare brand options as objects with value and label
  const brandOptions = availableBrands.map((brandName) => {
    if (brandName === "Select All") {
      return { value: "select_all", label: t("Tout") };
    } else {
      return {
        value: brandName,
        label: brandName,
      };
    }
  });

  // Prevent scrolling when overlay is visible
  useEffect(() => {
    const preventScroll = (e) => {
      e.preventDefault();
    };

    if (isOverlayVisible) {
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("wheel", preventScroll, { passive: false });
    } else {
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
    }

    return () => {
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
    };
  }, [isOverlayVisible]);

  const handleLoadMore = () => {
    setProductsToShowCount((prevCount) => prevCount + 48);
  };

  return (
    <div>
      {isOverlayVisible && (
        <div
          className="invisible-overlay"
          onClick={() => setOverlayVisible(false)}
        ></div>
      )}
      <div className="dropdown-container">
        <Dropdown2
          label={t("Marques")}
          options={brandOptions}
          selectedOptions={selectedBrands}
          setSelectedOptions={setSelectedBrands}
          setOverlayVisible={setOverlayVisible}
        />
        <Dropdown2
          label={t("Catégories")}
          options={categoryOptions}
          selectedOptions={selectedCategories}
          setSelectedOptions={setSelectedCategories}
          setOverlayVisible={setOverlayVisible}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="products-grid-unique">
            {displayedProducts.map((product, index) => (
              <div
                key={product.id}
                className="product-wrapper"
                onClick={() => handleProductClick(product)}
              >
                <div className="product-container-unique">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="product-image-unique"
                    loading="lazy"
                  />
                </div>
                <div className="product-details-unique">
                  <div className="new-in-unique">
                    {product.newIn ? t("Remote Bespoke") : <span>&nbsp;</span>}
                  </div>
                  <div className="item-name-unique">{product.name}</div>
                  <div className="price-unique">
                    {
                      pricePlusSurcharge(
                        +product.price.replace(/[^0-9.-]+/g, ""),
                        currency,
                        exchangeRates
                      ).formatted
                    }
                  </div>
                </div>
              </div>
            ))}
          </div>

          {displayedProducts.length < filteredProducts.length && (
            <div className="load-more-container">
              <span onClick={handleLoadMore} className="load-more-text">
                {t("Load More")} | {displayedProducts.length} {t("of")}{" "}
                {filteredProducts.length}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Products;
